import { defineNuxtPlugin, useRouter, watch, ref, useRoute, navigateTo, useDevice, useUserInfo, useWebauthn, until, useMarketingAutomationClient, useCoreConfig, useEventListener, useWindowScroll, useTracking } from '#imports'

interface MitIdBrowserResult {
    url?: string
    type?: 'success' | 'dismiss' | 'locked' | 'opened' | 'cancel' | 'access_denied' // Success, dismiss = App Tab browser was closed by user
}

export default defineNuxtPlugin({
    name: 'react-handles',
    parallel: true,
    setup(nuxtApp) {
        const router = useRouter()
        const route = useRoute()
        const { client } = useMarketingAutomationClient()
        const { isLoggedIn, data: userData } = useUserInfo()
        const { isIos } = useDevice()
        const config = useCoreConfig()
        const { trackEvent } = useTracking()

        nuxtApp.hooks.hookOnce('app:suspense:resolve', async () => {
            const { isMobileApp, useGetPasskeys, hasPasskeyOnDevice } = useWebauthn()

            if (!isMobileApp) return

            const { data: passkeys, isLoading } = useGetPasskeys({
                enabled: isLoggedIn
            })

            // Scroll
            const { y } = useWindowScroll(window)
            const scrollIsAtTop = ref<boolean>(true)
            // Set initial scrollIsTop value
            if (y.value > 0) {
                scrollIsAtTop.value = false
                emitScrollTop(scrollIsAtTop.value)
            }

            // Scroll
            // event listening - ensure we'll only be able to "pull-down to refresh" in react native when the value is true
            watch(
                () => y.value === 0,
                (isAtTop, wasAtTop) => {
                    if (isAtTop !== wasAtTop) {
                        scrollIsAtTop.value = isAtTop
                        emitScrollTop(scrollIsAtTop.value)
                    }
                }
            )

            if (isIos) {
                checkCookieBotLoaded()
            }

            useEventListener(window, 'CookiebotOnAccept', () => {
                const consent = window.Cookiebot?.consent
                window.ReactNativeWebView.postMessage(JSON.stringify({
                    type: 'cookiebot_accepted',
                    marketing: consent?.marketing,
                    statistics: consent?.statistics,
                    preferences: consent?.preferences,
                }))
            })

            useEventListener(window, 'CookiebotOnDecline', () => {
                const consent = window.Cookiebot?.consent
                window.ReactNativeWebView.postMessage(JSON.stringify({
                    type: 'cookiebot_declined',
                    marketing: consent?.marketing,
                    statistics: consent?.statistics,
                    preferences: consent?.preferences,
                }))
            })

            // Emit which environment is active (site)
            window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'env', env: 'site' }))

            // Wait for passkeys to have loaded if user is logged in
            if (isLoggedIn.value) {
                await until(() => isLoading.value).toBe(false)
            }

            // Emit loaded event to react-container
            // Add user passkey status
            window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'loaded', isLoggedIn: isLoggedIn.value, hasPasskey: isLoggedIn.value ? hasPasskeyOnDevice(passkeys?.value?.data || []) : false }))
        })

        function emitScrollTop(val: boolean) {
            window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'scroll', top: val }))
        }

        function checkCookieBotLoaded() {
            const duration = 5_000
            const startTime = Date.now()

            const interval = setInterval(() => {
                // Stop the interval if the duration has passed
                if (Date.now() - startTime > duration) {
                    clearInterval(interval)

                    return
                }

                if (window.Cookiebot) {
                    // Send a message and stop the interval
                    clearInterval(interval)

                    window.Cookiebot.hide() // Hide the banner until the user has made a choice regarding tracking in the native ios tracking prompt

                    if (window.Cookiebot.declined) return

                    window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'cookiebot_loaded' }))
                }
            }, 500)
        }

        // Add website handles
        window.websiteFunctions = {
            goBack: () => {
                if (route.meta.isPlaygame) {
                    return
                }

                router.back()
            },
            eidFunctions: {
                async handleMitIdBrowserResult(result?: MitIdBrowserResult) {
                    if (!result) return

                    const updateUrl = async (inputUrl: string | null, params = {}) => {
                        // If no URL is provided, fall back to the current page’s URL
                        const finalUrlString = (inputUrl ?? window.location.href).replace('spilnu://', `${window.location.origin}/`)

                        const newUrl = new URL(finalUrlString)

                        // Remove '/eid/mitid/' from the path if present
                        // For instance, if pathname was '/eid/mitid/konto/eid', it becomes '/konto/eid'
                        newUrl.pathname = newUrl.pathname.replace('/eid/mitid', '')

                        // Clear and re-set the search parameters
                        newUrl.search = ''
                        for (const [key, value] of Object.entries(params)) {
                            newUrl.searchParams.set(key, encodeURIComponent(value as string))
                        }

                        // Redirect to the updated URL
                        navigateTo(newUrl.toString(), { external: true })
                    }

                    const isNotSuccess = 'type' in result && result.type !== 'success'
                    const isDismissedOrCancelled = result.type && ['dismiss', 'cancel', 'access_denied'].includes(result.type)
                    const isAbortedOrAccessDenied = result.url && ['aborted', 'access_denied'].some(c => result.url!.includes(c))

                    trackEvent({
                        event_name: `app_mitid_browser_result`,
                        event_type: result?.type || 'unknown',
                        meta_data: [{ data: JSON.stringify(result) }],
                        log_entry: true
                    })

                    if ((isNotSuccess && isDismissedOrCancelled) || isAbortedOrAccessDenied) {
                        await updateUrl(null, {
                            error: 'dismissed_or_cancelled',
                            error_description: 'User dismissed or otherwise closed the MitId window',
                            mitid: 'true',
                            mitid_provider: 'unknown',
                        })

                        return
                    }

                    if (!result.url) return
                    // Extract the existing query parameters from the provided URL
                    const url = new URL(result.url)
                    const searchParams = Object.fromEntries(url.searchParams.entries())

                    await updateUrl(result.url, searchParams)
                },
            },
            declineTracking: () => {
                /* Handle tracking decline */
                window?.Cookiebot?.submitCustomConsent(false, false, false, false)
                window?.Cookiebot?.hide()
            },
            acceptTracking: () => {
                /* Handle tracking acceptance - show cookie banner */
                if (!window?.Cookiebot?.consented && !window.Cookiebot.declined) {
                    window?.Cookiebot?.show()
                }
            },
            async receiveAppleAdsToken(token: string) {
                try {
                    await client.POST('/v1/public/apple/ads/attribution/token/', {
                        body: {
                            apple_ads_token: token,
                            ...(isLoggedIn.value && { player_id: userData?.value?.playerId })
                        }
                    })
                }
                catch (error) {
                    trackEvent({
                        event_name: 'apple_ads_attribution_token_error',
                        event_type: 'get',
                        meta_data: [{ data: JSON.stringify({ error }) }]
                    })
                }
            },
            async receiveNotificationToken(token: string, state: 'active' | 'disabled') {
                try {
                    await client.POST('/v1/public/apptoken/', {
                        body: {
                            app_token: token,
                            ...(isLoggedIn.value && { player_id: userData?.value?.playerId }),
                            os: isIos ? 'iOS' : 'Android',
                            state,
                            brand: config.site.brand
                        }
                    })
                }
                catch (error) {
                    trackEvent({
                        event_name: 'app_notification_token_error',
                        event_type: 'update',
                        meta_data: [{ data: token, error }]
                    })
                }
            }
        }
    },
})
